<template>
  <div>
    <form @submit.prevent=" performUpdate()">
      <div class="form-container">
        <div v-for="(file, index) in files" :key="index" class="form-group">
          <span v-if="file.key" class="url">url: ../{{ getURL(file) }}</span>
          <table class="file-to-replace">
            <tbody>
              <tr>
                <td v-if="file.alias">name:</td>
                <td v-if="file.alias">{{ file.alias }}</td>
                <td v-if="file.accepts == 'image/*' && !file.multiple && isReplace" rowspan="3">
                  <img :src="file.dynamicURL" />
                </td>
              </tr>
                <tr v-if="file.userEmail">
                  <td>createdBy:</td><td> {{ file.userEmail }}</td>
              </tr>
              <tr v-if="file.accepts">
                <td>accepts:</td>
                <td>
                  {{ file.accepts }}
                  <span v-if="file.multiple">(multiple)</span>
                </td>
              </tr>
              <tr v-if="getNumPages(file) > 0">
                <td>pages:</td><td> {{ getNumPages(file) }}</td>
              </tr>
              <tr>
                <td colspan="2">
                  <div class="input-group">
                    <label :for="index">
                        <span
                          v-if="selectedFiles[index].status === FILE_SELECT_PROCESSING"
                          class="processing-container"
                        >
                          <span>Processing</span>
                          <Spinner
                            :size="13"
                            :color="'#FF5500'"
                            :borderWidth="8"
                          ></Spinner>
                        </span>
                        <span v-else>Select File ...</span>
                    </label>
                    <input
                      type="file"
                      :id="index"
                      ref="fileInput"
                      :accept="file.accepts"
                      :multiple="file.multiple"
                      @change="filesSelected(index)"
                      class="form-control"
                    >
                  </div>
                </td>
              </tr>
              <tr v-if="selectedFiles[index].files.length > 0
                && files[index].canUpdateDisplayName !== false">
                <td colspan="3">
                  <div class="alias-update">
                    <span>Display Name:</span>
                    <input
                      type="text"
                      v-model="selectedFiles[index].files[0].alias"
                      @input="restrictInput($event, index)"
                    >
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="selected-files-container">
            <div
              v-for="(file, fileIndex) in selectedFiles[index].files"
              :key="fileIndex"
              class="selected-file-container"
            >
              <span v-if="selectedFiles[index].files.length < 13">
                <b v-if="selectedFiles[index].files.length === 1">Selected file:</b> {{ file.name }}
              </span>
              <div class="file-details">
                <ProgressBar
                  :alwaysShow="true"
                  :percentage="file.uploadPercentage" />
                <img
                  v-if="isImage(file.file)"
                  :src="file.previewImage"
                  alt="Preview"
                  :height="25"
                />
              </div>
            </div>
          </div>
          <div class="tags-container">
            <div v-for="tag in eligibleTags" :key="tag.id">
              <div
                class="tag-container"
                :class="{ active: selectedFiles[index].tags.includes(tag.id) }"
                :tag-id="tag.id"
                @click="toggleTag(tag.id, index)"
              >
                <span class="tag">{{ tag.name }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Message
        :message="message.message"
        :level="message.level"
      ></Message>
      <button
        type="submit"
        class="btn btn-primary"
        :disabled="!isSubmitPossible || isUploadInitiated"
      >
        {{ buttonText }}
        <Spinner
          v-if="isUploadInitiated"
          :size="13"
          :color="'#FF5500'"
          :borderWidth="8"
        ></Spinner>
      </button>
    </form>
  </div>
</template>
<script>

import projectconfig from '@/project-config';

import createObject from '@/handlers/createObject';
import deleteObjects from '@/handlers/deleteObjects';
import dynamoDB from '@/aws/aws-dynamoDB';

import arrayEqual from '@/utils/arrayEqual';
import fileGetPages from '@/utils/fileGetPages';
import removeEmpty from '@/utils/objectRemoveEmpty';

import ProgressBar from '@/components/progress-bar.vue';
import Spinner from '@/components/spinner.vue';
import Message from '@/components/message.vue';

export default {
  components: {
    ProgressBar,
    Spinner,
    Message,
  },
  props: {
    files: {
      type: Array,
      required: true,
    },
    topicKey: {
      type: String,
      required: true,
    },
    securityLevel: {
      type: String,
      required: true,
      validator(value) {
        // 'information' will be stored in a public space that is available to anybody
        return ['information', 'private'].includes(value);
      },
    },
    isReplace: {
      type: Boolean,
      default: false,
    },
    unionHash: {
      type: String,
      required: true,
    },
    successCallback: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      selectedFiles: [],
      eligibleTags: [],
      message: {
        message: '',
        level: 'info',
      },
      FILE_SELECT_INACTIVE: 0,
      FILE_SELECT_PROCESSING: 1,
      FILE_SELECT_COMPLETED: 2,
      FILE_UPLOAD_INITIATED: 3,
    };
  },
  computed: {
    isSubmitPossible() {
      if (this.isReplace) {
        // If nothing has been selected and nothing has been altered
        // This check must be before the next one or, in case of form where no
        // files are "required", we always return true
        if (this.selectedFiles.every((e, index) => !this.isFileSelected(index)
          && !this.isTagsAltered(index))) {
          return false;
        }

        return this.selectedFiles.every((file, index) => this.isFileSelected(index)
          || !file.originalFile.required
          || (this.isTagsSelected(index) && this.isTagsAltered(index)));
      }
      // Creating file
      // All files MUST be selected as well as tags (if any)
      return this.selectedFiles.every((e, index) => this.isTagsSelected(index)
        && this.isFileSelected(index));
    },
    isUploadInitiated() {
      return this.selectedFiles.every(input => input.status === this.FILE_UPLOAD_INITIATED);
    },
    isUploadCompleted() {
      return this.selectedFiles.every(input => input.status === this.FILE_UPLOAD_INITIATED
      && input.files.every(file => file.uploadPercentage === 100));
    },
    buttonText() {
      if (this.isUploadInitiated) {
        return 'Updating ...';
      }

      if (this.isReplace) {
        return this.files.length === 1 ? 'Replace File' : 'Replace File(s)';
      }
      return this.files.length === 1 ? 'Add File' : 'Add File(s)';
    },
  },
  watch: {
    isUploadCompleted(newValue) {
      if (newValue) {
        this.successCallback(this.topicKey);
      }
    },
  },
  /**
   * Lifecycle hook that is called when the component is created
   *
   * queries the database for the file to be replaced. This is necessary
   * because only certain data exists in the database, not the file itself.
   *
   * @async
   */
  async created() {
    this.selectedFiles = this.files.map(file => ({
      files: [],
      originalFile: file,
      onSelectFilesMapper: file.onSelectFilesMapper
        ? file.onSelectFilesMapper : selctedFiles => selctedFiles,
      status: this.FILE_SELECT_INACTIVE,
      tags: (file.metadata && file.metadata.tags ? file.metadata.tags : []).map(tag => tag.id),
    }));

    const tags = await dynamoDB.getDBTagsByTopic(this.topicKey);
    this.eligibleTags = tags.sort((a, b) => a.name.localeCompare(b.name));
  },
  methods: {
    /**
     * Uploads a file to an S3 bucket
     *
     * @async
     * @throws {Error}
     *
     * @param {File} file - The file to be uploaded
     * @param {Object} filemetadata - The metadata of the file to be uploaded.
     *  if the fileuploader has been instantiated with the isReplace prop set to true
     *  then the filemetadata will also hold the metadata of the file to be replaced.
     * @param {Function} fileProgressCallback - A callback function that is called
     * when the file upload progress changes
     * @param {Function} fileSuccessCallback - A callback function that is called
     * when the file upload is completed
     *
     * @returns {void}
     */
    async uploadFile(file, filemetadata, fileProgressCallback, fileSuccessCallback) {
      // Generate a unique key using the current timestamp and the original file name
      // file name w/o extension
      const [name] = file.name.split('.');
      const alias = filemetadata.alias ? filemetadata.alias : this.generateAlias(name);
      // Depending on if the file is being uploaded or replaced, the key will be either
      // a new one or the same as the file being replaced. This is because AWS S3 buckets
      // work by having a key that is unique to each file. And if the file is replaced
      // the key will be the same but the file will be overwritten.
      // This is critical to the feature of replacing files while maintaining the same
      // url that has been sent to clients.
      const prefix = [this.securityLevel, this.topicKey].join('/');
      let key = '';
      if (this.isReplace && filemetadata.key) {
        ({ key } = filemetadata);
      } else {
        const keyPrefix = `${prefix}/${this.generateFileName(name)}-`;
        key = `${keyPrefix}${await this.generateUniqueHashSuffix(keyPrefix)}`;
      }

      const metadata = removeEmpty({
        numPages: await fileGetPages(file),
        size: file.size,
        lastModified: file.lastModified,
        uploadedTimestamp: Date.now(),
        unionHash: this.unionHash,
        type: file.type,
        tags: this.eligibleTags
          .filter(tag => filemetadata.tags.includes(tag.id))
          .map(tag => tag.id),
      });

      createObject(
        file,
        key,
        alias,
        metadata,
        (progressEvent) => {
          // calculate upload percentage
          fileProgressCallback(progressEvent);
        },
        (successResult) => {
          // Reset the upload percentage
          fileSuccessCallback(successResult);
        },
        (error) => {
          this.$set(this, 'message', {
            message: error.message ? error.message : error,
            level: 'error',
          });
        },
      );
    },
    async uploadFiles(input, inputIndex) {
      this.$set(this.selectedFiles[inputIndex], 'status', this.FILE_UPLOAD_INITIATED);
      if (input.files.length === 0) {
        return;
      }

      if (this.isReplace) {
        // I a file has been selected for replacement
        // If this component has been instantiated with a file that has a key
        // this means that specific file is being replaced. Otherwise, all files
        // with the same unionHash are being replaced.
        if (Object.keys(input.originalFile).includes('key')) {
          // Delete one specific file
          await deleteObjects(input.originalFile.key);
        } else {
          // Delete entire file cluster
          const dbFiles = await dynamoDB.getDBFilesByUnionHash(this.unionHash);
          await deleteObjects(dbFiles.map(file => file.key));
        }
      }

      const filesUploads = input.files.map(file => this.uploadFile(
        file.file,
        {
          ...input.originalFile,
          alias: file.alias,
          tags: this.selectedFiles[inputIndex].tags,
        },
        (progressEvent) => {
          if (typeof file === 'undefined') {
            return;
          }

          const uploadPercentage = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          // We get from successCallback
          if (uploadPercentage === 100) {
            return;
          }
          // When uploading several files which files progress is at what state seems to get
          // tangled. Unclear what could cause this. Essentially we get updated with object that
          // looks like this:
          // {loaded: 90357760, total: 272824405, part: 19,
          //    Key: 'private/key_image_3d/images-U3', Bucket: 'seenthis-brandportal92332-rhymedev'}
          // for some reason the same Key can get several "part: 19" (in this example). And total
          // comes back in the wrong order.
          //
          // What this statment does is just make sure that percentage uploaded can never go
          // backwards.
          // While this is not a "solution" to the problem this is "good enough" for these reasons:
          // 1. The basis for progresscallback functiontality is visual suger for users.
          // 2. The effort and complexity to solve this could be enormous.
          // So the conclusion is the juice is not worth the squeeze.
          if (file.uploadPercentage > uploadPercentage) {
            return;
          }

          this.$set(file, 'uploadPercentage', uploadPercentage);
        },
        () => {
          if (typeof file === 'undefined') {
            return;
          }
          this.$set(file, 'uploadPercentage', 100);
        },
      ));

      await Promise.allSettled(filesUploads);
    },
    async updateMetaData(input, inputIndex) {
      try {
        // origianl file key
        const { key } = input.originalFile;
        // same metadata as original file, but with updated tags
        const metadata = this.removeEmpty({
          ...input.originalFile.metadata,
          tags: this.eligibleTags
            .filter(tag => input.tags.includes(tag.id))
            .map(tag => tag.id),
        });

        const params = {
          id: key,
          metadata,
        };
        await dynamoDB.updateDBFile(params);

        this.$set(this.selectedFiles[inputIndex].files[0], 'uploadPercentage', 100);
      } catch (error) {
        this.$set(this, 'message', {
          message: error.message ? error.message : error,
          level: 'error',
        });
      }
    },
    async performUpdate() {
      this.selectedFiles.forEach(async (input, inputIndex) => {
        // Inversion: if only metadata has been update (ie. tags) we only move to update
        // file database reference and leave the file itself untouched.
        if (this.isReplace
          && !this.isFileSelected(inputIndex)
          && this.isTagsSelected(inputIndex)
          && this.isTagsAltered(inputIndex)
        ) {
          this.$set(this.selectedFiles[inputIndex], 'files', [{ uploadPercentage: 0 }]);
          return this.updateMetaData(input, inputIndex);
        }


        // Default behaviour: if the file has been altered, we upload the file and update
        // this will also update the file metadata.
        return this.uploadFiles(input, inputIndex);
      });
    },
    async getFileMeta(file, alias) {
      // file name w/o extension
      const [name] = file.name.split('.');
      const metaData = {
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
        numPages: await fileGetPages(file),
        alias: alias || this.generateAlias(name),
        previewImage: '',
        uploadPercentage: 0,
      };

      // Read the file and set the previewImage property
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataURL = event.target.result;
        const mimeType = dataURL.split(',')[0].split(':')[1].split(';')[0];

        metaData.previewImage = mimeType.startsWith('image/') ? dataURL : null;
      };

      await reader.readAsDataURL(file);

      return metaData;
    },
    /**
     * Updates the value of the `numPages` data property when a new file is
     * selected in the input element.
     */
    async filesSelected(index) {
      const input = this.$refs.fileInput[index];
      if (input.files.length === 0) {
        return;
      }

      // Relove eventual error that has been shown previously
      this.clearError();
      // Reset selected files and their status
      this.selectedFiles[index].status = this.FILE_SELECT_INACTIVE;
      this.selectedFiles[index].files = [];

      // Restric input if files with disfigured names as this will
      // be persisted as the key for the object (ie the customer phasing url)
      // Note that this only applys to public files, ie 'information'
      if (!this.isReplace && this.securityLevel === 'information') {
        const [fileName, suffix] = input.files[0].name.split('.');
        // If, when we run the generateFileName algorithm, the file name is changed
        // this means that the file name is not valid
        if (fileName !== this.generateFileName(fileName)) {
          this.message = {
            message: `File name can only contain lower case letters, numbers and dashes. Suggestion: ${this.generateFileName(fileName)}.${suffix}`,
            level: 'warning',
          };
          return;
        }
      }

      this.selectedFiles[index].status = this.FILE_SELECT_PROCESSING;

      try {
        const selectedFiles = Array.from(input.files);
        // Allows the files to be mapped by parameter to the component
        // By default this will just return the selectedFiles as they were.
        // Example of use case:
        // When we upload images packs this mapper both zip the original images and also minify them
        const mappedFiles = await this.selectedFiles[index].onSelectFilesMapper(selectedFiles);

        const alias = this.isReplace ? this.files[index].alias : null;
        // Generate file metadata for all processed files
        // Note that these are not the original files
        const filesMeta = mappedFiles.map(file => this.getFileMeta(file, alias));
        const results = await Promise.allSettled(filesMeta);

        this.$set(this.selectedFiles[index], 'files', results.map(res => res.value));

        this.selectedFiles[index].status = this.FILE_SELECT_COMPLETED;
      } catch (e) {
        this.selectedFiles[index].status = this.FILE_SELECT_INACTIVE;
        this.message = {
          message: e.message,
          level: 'error',
        };
      }
    },
    /**
     * Generate a unique file name based on the original file name, current date and number of pages
     *
     * @param {File} file - The file object to generate name for
     *
     * @return {Promise<string>} - A promise that resolves to a string representing the
     *    generated file name
     */
    generateAlias(name) {
      const alias = name
        .replace(/-/g, ' ') // Replace dashes with spaces
        .replace(/\s{2}/g, ' ') // Replace multiple spaces with single spaces
        .replace(/\b\w/g, match => match.toUpperCase()) // Capitalize the first letter of each word
        .replace(/[^a-zA-Z0-9\s]/g, ''); // Remove all characters except letters, numbers, and spaces
      return alias;
    },
    generateFileName(alias) {
      const name = alias
        .replace(/\s/g, '-') // Replace spaces with dashes
        .toLowerCase() // Convert to lowercase
        .replace(/[^a-z0-9-]/g, ''); // Remove all characters except letters, numbers, and dashes
      return name;
    },
    /**
     * Generate a random hash suffix to enable multiple files with the same name to be uploaded
     * Specification: 2 characters, first D-V, second 2-9
     *
     * @return {string} - A string representing the generated hash suffix
     */
    generateHashSuffix() {
      const characters = 'DEFGHIJKLMNOPQRSTUV';
      const numbers = '23456789';

      return `${characters[Math.floor(Math.random() * characters.length)]}${numbers[Math.floor(Math.random() * numbers.length)]}`;
    },
    /**
     * Generates a unique hash suffix for a file with the given key prefix.
     * Searches the database for files with the same key prefix and generates
     * a suffix that is not already in use.
     *
     * @param {string} keyPrefix
     *
     * @return {string} - A string representing the generated hash suffix
     */
    async generateUniqueHashSuffix(keyPrefix) {
      // Get all files with the same key prefix
      const filesWithSameKey = await dynamoDB.getDBFilesByKeyPrefix(keyPrefix);
      // Get all suffixes of files with the same key prefix
      const activeSuffixes = filesWithSameKey.map(file => file.key.split('-').pop());
      // Generate a suffix. If it already exists, generate new ones until
      // a unique one is found
      let uniqueSuffix = this.generateHashSuffix();
      while (activeSuffixes.includes(uniqueSuffix)) {
        uniqueSuffix = this.generateHashSuffix();
      }

      return uniqueSuffix;
    },
    isImage(file) {
      return file && file.type.startsWith('image/');
    },
    getNumPages(file) {
      const numPages = file && file.metadata && file.metadata.numPages;
      if (numPages) {
        return numPages;
      }
      return 0;
    },
    toggleTag(tagId, fileIndex) {
      const { tags } = this.selectedFiles[fileIndex];
      if (tags.includes(tagId)) {
        this.$set(this.selectedFiles[fileIndex], 'tags', tags.filter(id => id !== tagId));
      } else {
        tags.push(tagId);
        this.$set(this.selectedFiles[fileIndex], 'tags', tags);
      }
    },
    isTagsSelected(fileIndex) {
      return this.eligibleTags.length === 0 || this.selectedFiles[fileIndex].tags.length > 0;
    },
    isTagsAltered(fileIndex) {
      const selectedFile = this.selectedFiles[fileIndex];
      const file = selectedFile.originalFile;
      const fileTags = file.metadata && file.metadata.tags ? file.metadata.tags : [];
      // simple comparison of tags between selected file and original file
      return !arrayEqual(selectedFile.tags, fileTags.map(tag => tag.id));
    },
    /**
     * If a file has been selected and is not being processed, it is considered selected.
     *
     * @param {*} fileIndex
     */
    isFileSelected(fileIndex) {
      return this.selectedFiles[fileIndex].status === this.FILE_SELECT_COMPLETED;
    },
    restrictInput(event, index) {
      const alias = event.target.value;
      const correctAlias = this.generateAlias(alias);
      if (alias !== correctAlias) {
        this.$set(this.selectedFiles[index].files[0], 'alias', correctAlias);
        this.$set(this, 'message', {
          message: 'Display name can only contain letters, numbers and spaces. Words will be capitalized.',
          level: 'info',
        });
      } else {
        this.clearError();
      }
    },
    getURL(file) {
      if (projectconfig.URL_FORMATTING.MODE === 'RELATIVE') {
        return file.dynamicURL;
      }
      return file.Key;
    },
    clearError() {
      this.$set(this, 'message', {
        message: '',
        level: 'info',
      });
    },
  },
};
</script>
<style lang="stylus" scoped>
  @require '../style/colors'

  .form-container
    display: flex
    flex-direction: row
    justify-content: center
    align-items: flex-start
    flex-wrap: wrap;
    gap: 4rem;

    .form-group
      display: flex
      flex-direction: column
      align-items: center
      justify-content: center
      gap: 5px
      max-width: 550px

    .selected-files-container
      display: flex
      flex-flow: column wrap
      align-items: stretch
      gap: 3px 1rem
      width: 100%
      max-height: 330px
      .selected-file-container
        display: flex
        justify-content: center

        .file-details
          display: flex
          flex-direction: row;
          flex-wrap: nowrap
          align-items: center
          gap: 2px
          width: 100%

        span
          position: absolute;
          font-size: 11px
          margin-right: 30px

  table
    width: 100%
    border-collapse: collapse
    background-color: $secondary-color
    box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.25)

    th
      background-color: $secondary-color
      color: $white
      font-weight: bold
      font-size: 18px
      padding: 6px
      text-align: left

    td
      font-size: 14px
      padding: 10px
      text-align: left
      border-bottom: 1px solid $green30
      &:nth-child(1)
        font-weight: bold
        text-align: left
      &:nth-child(2)
        text-align: right
        min-width: 240px
      &:nth-child(3)
        text-align: center
        background-color: $white
        border: 1px solid $black30

    tr:last-child td
      border-bottom: none

    tr:hover
      background-color: $white

    tr:hover td
      color: $blue

    img
      height: 9rem

  form
    display: flex
    flex-direction: column
    align-items: center
    justify-content: center
    gap: 1rem

    div
      display: flex
      flex-direction: column
      align-items: center

    label
      width: 200px
      padding: 1.3rem 1.3rem
      border: 1px solid $black30
      border-radius: .5em
      cursor: pointer
      text-align: center
      font-size: 1.5rem
      transition: border-color 0.3s ease-in-out
      background-color: $white

      &:focus
        border-color: $primary-color
        background-color: darken($primary-color, 10%)

    input[type="file"]
      display: none

    button[type="submit"]
      display: flex
      flex-flow: row nowrap
      align-items: center
      gap: 2rem
      background-color: $primary-color
      color: $white
      font-size: 1.7rem
      padding: 1.3rem 2.5rem
      border: none
      border-radius: .5rem
      cursor: pointer
      transition: background-color 0.3s ease-in-out

      &:hover
        background-color: darken($primary-color, 10%)

      &:disabled
        /* Disabled state styles */
        background-color: $secondary-color;
        cursor: not-allowed;
  .processing-container
    display: flex
    flex-direction: row
    flex-wrap: nowrap
    align-items: center
    justify-content: center
    gap: 16px

  .tags-container
    display: flex
    flex-flow: row wrap
    justify-content: center
    align-items: center
    gap: .5rem

    .tag-container
      display: flex
      align-items: center
      padding: .5rem 1.5rem
      border-radius: 2rem
      border: 2px solid $primary-color
      color: $primary-color
      cursor: pointer
      transition: all 0.3s ease-in-out
      font-size: 1.5rem

      &:hover
        background-color: $primary-color
        color: $white
        transform: scale(1.1)

      &.active
        background-color: $primary-color
        color: $white

  .alias-update
    display: flex
    flex-flow: row nowrap
    justify-content: center
    gap: 1rem

    span
      font-size: 1.5rem
      font-weight: bold
      white-space: nowrap

    input
      padding: 1rem
      border: 1px solid $black30
      border-radius: .5em
      min-width: 50%

  .url
    font-size: 15px
    color: $black60
    align-self: flex-start

</style>
